import { PencilIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { type ReactNode } from 'react';

import { CitationViewer, type EvidenceData } from '@/components/citations';
import { SuggestionBadge } from '@/components/suggestions';
import type { EntitySidecar } from '@/services/api/generated/subjectsService';

interface EntityCardProps {
  /** Main card content */
  children: ReactNode;
  /** Icon to display (e.g., UserIcon, BuildingOfficeIcon) */
  icon: ReactNode;
  /** Whether this entity is the claimant */
  isClaimant?: boolean;
  /** Approval metadata for suggestions */
  approvalMetadata?: EntitySidecar | null;
  /** Whether to show approval badge */
  showApproval?: boolean;
  /** Edit handler */
  onEdit?: () => void;
  /** Evidence data for citation viewer */
  evidence?: EvidenceData[];
  /** Title for evidence dialog */
  evidenceTitle?: string;
  /** Matter ID for citations */
  matterId?: string;
}

/**
 * Base entity card component providing consistent structure and styling
 * for all entity types (persons, organizations, vehicles, properties, animals)
 */
export const EntityCard = ({
  children,
  icon,
  isClaimant = false,
  approvalMetadata,
  showApproval = false,
  onEdit,
  evidence = [],
  evidenceTitle,
  matterId,
}: EntityCardProps) => (
  <div
    className={clsx(
      'group relative rounded border p-3 transition-colors',
      isClaimant ? 'border-blue-400 bg-blue-50/60' : 'border-gray-200 bg-white',
    )}
  >
    {/* Hover controls */}
    {(onEdit || (evidence.length > 0 && matterId)) && (
      <div className="absolute top-2 right-2 z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {evidence.length > 0 && matterId && evidenceTitle && (
          <CitationViewer
            evidence={evidence}
            matterId={matterId}
            dialogTitle={evidenceTitle}
            compactTrigger
            buttonVariant="ghost"
            buttonClassName="p-1 h-7 w-7 hover:bg-gray-100 text-gray-600"
          />
        )}
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="p-1 hover:bg-gray-100 rounded"
            aria-label="Edit"
          >
            <PencilIcon className="h-4 w-4 text-gray-600" />
          </button>
        )}
      </div>
    )}

    {/* Main content */}
    <div className="flex items-start gap-2.5">
      <div
        className={clsx(
          'h-5 w-5 mt-0.5 flex-shrink-0',
          isClaimant ? 'text-blue-600' : 'text-gray-400',
        )}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        {showApproval && approvalMetadata && (
          <div className="mb-2">
            <SuggestionBadge approvalMetadata={approvalMetadata} />
          </div>
        )}
        {children}
      </div>
    </div>
  </div>
);

interface EntityCardHeaderProps {
  /** Display name/title */
  title: string;
  /** Whether this is the claimant */
  isClaimant?: boolean;
  /** Badges to display next to title */
  badges?: ReactNode[];
}

/**
 * Standardized header for entity cards
 */
export const EntityCardHeader = ({
  title,
  isClaimant = false,
  badges = [],
}: EntityCardHeaderProps) => (
  <div className="flex items-baseline gap-2 flex-wrap">
    <h3
      className={clsx(
        'font-semibold',
        isClaimant ? 'text-blue-900' : 'text-gray-900',
      )}
    >
      {title}
    </h3>
    {isClaimant && (
      <span className="rounded-full bg-blue-600 px-2 py-0.5 text-xs font-semibold uppercase text-white">
        Client
      </span>
    )}
    {badges.map((badge, idx) => (
      <span key={idx}>{badge}</span>
    ))}
  </div>
);

interface EntityCardSectionProps {
  children: ReactNode;
  /** Indentation level (default: 1 = ml-7) */
  indent?: number;
  className?: string;
}

/**
 * Content section within entity card (typically indented)
 */
export const EntityCardSection = ({
  children,
  indent = 1,
  className,
}: EntityCardSectionProps) => (
  <div className={clsx(indent === 1 && 'ml-7', className)}>{children}</div>
);
